import React from 'react';
import AlertTriangle from 'lucide-react/dist/esm/icons/alert-triangle';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2';
import LogOut from 'lucide-react/dist/esm/icons/log-out';

type DialogVariant = 'default' | 'danger' | 'warning';

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: DialogVariant;
  icon?: React.ReactNode;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  icon = null
}) => {
  if (!isOpen) return null;

  const getIcon = (): React.ReactNode => {
    if (icon) return icon;
    switch (variant) {
      case 'danger':
        return <Trash2 size={20} className="text-status-error" />;
      case 'warning':
        return <AlertTriangle size={20} className="text-status-warning" />;
      default:
        return <LogOut size={20} className="text-[#8b5cf6]" />;
    }
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-surface-overlay backdrop-blur-sm z-modal flex items-center justify-center animate-fade-in"
      onClick={handleOverlayClick}
    >
      <div 
        className="bg-gradient-to-br from-[var(--surface-modal-from)] to-[var(--surface-modal-to)] border border-[var(--surface-modal-border)] rounded-2xl p-8 max-w-[400px] w-[90%] shadow-[0_25px_50px_rgba(0,0,0,0.5)] animate-scale-in"
        role="dialog" 
        aria-modal="true"
      >
        <h3 className="text-[#f1f5f9] text-xl font-semibold mb-3 flex items-center gap-2">
          {getIcon()}
          {title}
        </h3>
        <p className="text-secondary text-[0.95rem] leading-relaxed mb-6">{message}</p>
        <div className="flex gap-3 justify-end">
          <button 
            className="py-2.5 px-5 rounded-lg font-medium text-[0.9rem] cursor-pointer transition-all duration-200 bg-transparent border border-strong text-secondary hover:bg-surface-primary hover:border-strong"
            onClick={onClose}
            autoFocus
          >
            {cancelText}
          </button>
          <button 
            className={`py-2.5 px-5 rounded-lg font-medium text-[0.9rem] cursor-pointer transition-all duration-200 border-none text-content hover:-translate-y-0.5 ${
              variant === 'danger' 
                ? 'bg-gradient-to-br from-status-error to-[#dc2626] hover:from-[#dc2626] hover:to-[#b91c1c]' 
                : 'bg-gradient-to-br from-[#8b5cf6] to-[#7c3aed] hover:from-[#7c3aed] hover:to-[#6d28d9]'
            }`}
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationDialog;
